import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { projectsApi } from '../services/api';
import {
  Search, ArrowUp, ArrowDown, ArrowUpDown,
  CheckCircle2, Circle, ChevronDown, ChevronRight, MessageSquare, Layers,
} from 'lucide-react';
import BusyBeeLogo from '../components/BusyBeeLogo';
import { PRIORITIES, DEFAULT_STATUSES, extractMultilineText } from '../lib/constants';

// Module-level constants - stable across renders
const priorityConfig = Object.fromEntries(
  Object.entries(PRIORITIES).map(([k, v]) => [k, { label: v.altLabel, color: v.combined }])
);
const statusConfig = {
  todo:        { label: 'To Do',       color: DEFAULT_STATUSES.todo.combined },
  in_progress: { label: 'In Progress', color: DEFAULT_STATUSES.in_progress.combined },
  completed:   { label: 'Done',        color: DEFAULT_STATUSES.completed.combined },
};
const PRIORITY_ORDER = { p1: 1, p2: 2, p3: 3, p4: 4 };
const STATUS_ORDER   = { todo: 1, in_progress: 2, completed: 3 };

const SortIcon = ({ column, sortCol, sortDir }) => {
  if (sortCol !== column) return <ArrowUpDown className="w-3 h-3 opacity-30" />;
  return sortDir === 'asc'
    ? <ArrowUp className="w-3 h-3 text-amber-400" />
    : <ArrowDown className="w-3 h-3 text-amber-400" />;
};

// Lightweight read-only Tiptap JSON renderer
const renderMark = (text, marks = []) => {
  let el = <>{text}</>;
  for (const mark of marks) {
    if (mark.type === 'bold')      el = <strong>{el}</strong>;
    else if (mark.type === 'italic')    el = <em>{el}</em>;
    else if (mark.type === 'strike')    el = <s>{el}</s>;
    else if (mark.type === 'underline') el = <u>{el}</u>;
    else if (mark.type === 'code')      el = <code className="px-1 py-0.5 rounded bg-white/10 text-amber-300 text-[0.85em] font-mono">{el}</code>;
    else if (mark.type === 'link')      el = <a href={mark.attrs?.href} target="_blank" rel="noopener noreferrer" className="text-amber-400 underline">{el}</a>;
  }
  return el;
};

const renderTiptapNode = (node, key) => {
  if (!node) return null;
  const ch = (node.content || []).map((c, i) => renderTiptapNode(c, i));
  switch (node.type) {
    case 'doc':       return <div key={key}>{ch}</div>;
    case 'paragraph': return <p key={key} className="mb-2 last:mb-0 text-sm text-gray-300 leading-relaxed">{ch.length ? ch : <br />}</p>;
    case 'text':      return <React.Fragment key={key}>{renderMark(node.text, node.marks)}</React.Fragment>;
    case 'hardBreak': return <br key={key} />;
    case 'heading': {
      const sizes = { 1:'text-lg font-bold', 2:'text-base font-bold', 3:'text-sm font-semibold' };
      return <p key={key} className={`${sizes[node.attrs?.level] || 'text-sm font-semibold'} text-white mb-1.5`}>{ch}</p>;
    }
    case 'bulletList':  return <ul key={key} className="list-disc pl-5 mb-2 space-y-0.5 text-sm text-gray-300">{ch}</ul>;
    case 'orderedList': return <ol key={key} className="list-decimal pl-5 mb-2 space-y-0.5 text-sm text-gray-300">{ch}</ol>;
    case 'listItem':    return <li key={key}>{ch}</li>;
    case 'blockquote':  return <blockquote key={key} className="border-l-2 border-gray-600 pl-3 mb-2 text-gray-400 italic text-sm">{ch}</blockquote>;
    case 'codeBlock':   return <pre key={key} className="bg-white/5 rounded p-3 mb-2 overflow-x-auto text-xs text-gray-300 font-mono"><code>{ch}</code></pre>;
    case 'horizontalRule': return <hr key={key} className="border-gray-700 my-3" />;
    default: return ch.length ? <React.Fragment key={key}>{ch}</React.Fragment> : null;
  }
};

const RichText = ({ content }) => {
  if (!content) return null;
  if (typeof content === 'string') return <p className="text-sm text-gray-300 whitespace-pre-wrap">{content}</p>;
  return renderTiptapNode(content, 'root');
};

const extractText = (content) => {
  if (!content) return '';
  if (typeof content === 'string') return content;
  if (typeof content === 'object') {
    if (content.type === 'text') return content.text || '';
    if (Array.isArray(content.content)) return content.content.map(extractText).join('');
    if (content.content) return extractText(content.content);
  }
  return '';
};

const formatDate = (dateStr) => {
  if (!dateStr) return null;
  const datePart = typeof dateStr === 'string' ? dateStr.split('T')[0] : String(dateStr);
  const [y, m, d] = datePart.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

// Build section groups from tasks + sections, sorted by sortCol/sortDir
const buildGroups = (tasks, sections, search, sortCol, sortDir) => {
  // 1. filter - completed tasks are never shown on the public page
  const filtered = tasks.filter(t => {
    if (t.status === 'completed') return false;
    if (search) {
      const q = search.toLowerCase();
      const title = (t.title || '').toLowerCase();
      const desc  = extractText(t.description).toLowerCase();
      if (!title.includes(q) && !desc.includes(q)) return false;
    }
    return true;
  });

  // 2. sort comparator (uses module-level constants)
  const cmp = (a, b) => {
    let v = 0;
    if (sortCol === 'priority')
      v = (PRIORITY_ORDER[a.priority] || 4) - (PRIORITY_ORDER[b.priority] || 4);
    else if (sortCol === 'due_date') {
      const da = a.due_date ? new Date(a.due_date).getTime() : Infinity;
      const db = b.due_date ? new Date(b.due_date).getTime() : Infinity;
      v = da - db;
    } else if (sortCol === 'status')
      v = (STATUS_ORDER[a.status] || 4) - (STATUS_ORDER[b.status] || 4);
    else if (sortCol === 'title')
      v = (a.title || '').localeCompare(b.title || '');
    return sortDir === 'asc' ? v : -v;
  };

  // 3. group by section
  if (sections.length === 0) {
    return [{ section: null, tasks: [...filtered].sort(cmp) }];
  }

  const bySection = new Map();
  sections.forEach(s => bySection.set(s.id, []));
  bySection.set(null, []);

  filtered.forEach(task => {
    const key = task.section_id && bySection.has(task.section_id) ? task.section_id : null;
    bySection.get(key).push(task);
  });

  const result = [];
  // Unsectioned tasks first (matches admin view - shown under project name)
  const unsectioned = [...(bySection.get(null) || [])].sort(cmp);
  if (unsectioned.length > 0) result.push({ section: null, tasks: unsectioned });
  // Named sections in their saved order
  sections.forEach(s => {
    const arr = [...(bySection.get(s.id) || [])].sort(cmp);
    if (arr.length > 0) result.push({ section: s, tasks: arr });
  });

  return result;
};

const SharedProjectPage = () => {
  const { shareToken } = useParams();
  const [project, setProject]   = useState(null);
  const [tasks, setTasks]       = useState([]);
  const [sections, setSections] = useState([]);
  const [ownerName, setOwnerName] = useState('');
  const [loading, setLoading]   = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [search, setSearch]               = useState('');
  const [sortCol, setSortCol]             = useState('priority');
  const [sortDir, setSortDir]             = useState('asc');
  const [expandedTaskIds, setExpandedTaskIds]         = useState(new Set());
  const [collapsedSectionIds, setCollapsedSectionIds] = useState(new Set());

  useEffect(() => {
    projectsApi.getShared(shareToken)
      .then(res => {
        setProject(res.data.project);
        setTasks(res.data.tasks || []);
        setSections(res.data.sections || []);
        setOwnerName(res.data.owner_name || '');
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [shareToken]);

  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };

  const toggleTask = (id) => setExpandedTaskIds(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const toggleSection = (id) => setCollapsedSectionIds(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const groupedSections = useMemo(
    () => buildGroups(tasks, sections, search, sortCol, sortDir),
    [tasks, sections, search, sortCol, sortDir]
  );

  const totalVisible   = groupedSections.reduce((n, g) => n + g.tasks.length, 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#111112] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-amber-500" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#111112] flex flex-col items-center justify-center gap-4 px-4">
        <div className="text-5xl">🔒</div>
        <h1 className="text-xl font-semibold text-white">This project is no longer shared</h1>
        <p className="text-gray-400 text-sm text-center">The link may have been revoked or expired.</p>
        <Link to="/register" className="mt-2 px-4 py-2 bg-amber-500 text-black font-medium rounded-lg hover:bg-amber-400 transition-colors text-sm">
          Create a free BusyBee.Day account
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#111112] text-white">
      {/* Top bar */}
      <header className="border-b border-gray-800 px-4 sm:px-8 py-3 flex items-center justify-between">
        <BusyBeeLogo iconOnly={false} className="h-8" />
        <Link
          to="/register"
          className="px-3 py-1.5 bg-amber-500 text-black text-sm font-medium rounded-lg hover:bg-amber-400 transition-colors"
        >
          Sign up free
        </Link>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        {/* Project header */}
        <div className="space-y-1">
          <div className="flex items-center gap-2.5">
            <div className="w-4 h-4 rounded flex-shrink-0" style={{ backgroundColor: project.color || '#6B7280' }} />
            <h1 className="text-2xl font-bold text-white">{project.name}</h1>
          </div>
          {project.description && (
            <p className="text-gray-400 text-sm pl-6">{project.description}</p>
          )}
          <p className="text-gray-500 text-xs pl-6">
            {tasks.length} task{tasks.length !== 1 ? 's' : ''}
            {ownerName && ` · Shared by ${ownerName}`}
          </p>
        </div>

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[180px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search tasks…"
              className="w-full bg-white/5 border border-gray-700 rounded-lg pl-8 pr-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-amber-500/60"
            />
          </div>
        </div>

        {/* Lists */}
        {totalVisible === 0 ? (
          <div className="border border-gray-800 rounded-xl px-4 py-10 text-center text-gray-500 text-sm">
            {search ? 'No tasks match your search.' : 'No tasks in this project.'}
          </div>
        ) : (
          <div className="space-y-4">
            {groupedSections.map(({ section, tasks: sectionTasks }) => {
              const sectionKey = section ? section.id : '__unsectioned__';
              const collapsed  = collapsedSectionIds.has(sectionKey);

              return (
                <div key={sectionKey} className="border border-gray-800 rounded-xl overflow-hidden">
                  {/* Section header */}
                  <button
                    onClick={() => toggleSection(sectionKey)}
                    className="w-full flex items-center gap-2 px-4 py-2.5 bg-white/[0.03] hover:bg-white/[0.05] transition-colors text-left border-b border-gray-800/60"
                  >
                    {collapsed
                      ? <ChevronRight className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
                      : <ChevronDown  className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
                    }
                    <Layers className="w-3 h-3 text-gray-600 flex-shrink-0" />
                    <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                      {section ? section.name : project.name}
                    </span>
                    <span className="ml-1 text-xs text-gray-600">{sectionTasks.length}</span>
                  </button>

                  {/* Column headers - desktop only, inside each card */}
                  {!collapsed && (
                    <div className="hidden sm:grid grid-cols-12 gap-2 px-4 py-2 bg-white/[0.02] border-b border-gray-800/40 text-xs text-gray-600 font-medium">
                      <button onClick={() => handleSort('title')} className="col-span-5 flex items-center gap-1 hover:text-gray-400 transition-colors text-left">
                        Task <SortIcon column="title" sortCol={sortCol} sortDir={sortDir} />
                      </button>
                      <button onClick={() => handleSort('status')} className="col-span-3 flex items-center gap-1 hover:text-gray-400 transition-colors">
                        Status <SortIcon column="status" sortCol={sortCol} sortDir={sortDir} />
                      </button>
                      <button onClick={() => handleSort('priority')} className="col-span-2 flex items-center gap-1 hover:text-gray-400 transition-colors">
                        Priority <SortIcon column="priority" sortCol={sortCol} sortDir={sortDir} />
                      </button>
                      <button onClick={() => handleSort('due_date')} className="col-span-2 flex items-center gap-1 hover:text-gray-400 transition-colors">
                        Due <SortIcon column="due_date" sortCol={sortCol} sortDir={sortDir} />
                      </button>
                    </div>
                  )}

                    {/* Task rows */}
                    {!collapsed && (
                      <div className="divide-y divide-gray-800/50">
                        {sectionTasks.map(task => {
                          const priority   = priorityConfig[task.priority] || priorityConfig.p4;
                          const status     = statusConfig[task.status] || statusConfig.todo;
                          const done       = task.status === 'completed';
                          const due        = formatDate(task.due_date);
                          const expanded   = expandedTaskIds.has(task.id);
                          const descText   = extractText(task.description);
                          const notesText  = extractText(task.notes);
                          const hasDetails = descText || notesText || task.due_time || (task.comments?.length > 0);

                          return (
                            <div key={task.id} onClick={() => hasDetails && toggleTask(task.id)}
                              className={hasDetails ? 'cursor-pointer' : ''}>

                              {/* Mobile card (hidden on sm+) */}
                              <div className="flex items-center gap-2.5 px-4 py-3 sm:hidden hover:bg-white/[0.02] transition-colors">
                                <div className="flex-shrink-0">
                                  {done
                                    ? <CheckCircle2 className="w-5 h-5 text-green-500" />
                                    : <Circle       className="w-5 h-5 text-gray-600" />
                                  }
                                </div>
                                <div className="min-w-0 flex-1">
                                  <span className={`text-sm truncate block ${done ? 'line-through text-gray-500' : 'text-white'}`}>
                                    {task.title}
                                  </span>
                                  <div className="flex flex-wrap items-center gap-1.5 mt-1">
                                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${status.color}`}>
                                      {status.label}
                                    </span>
                                    {task.priority !== 'p4' && (
                                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${priority.color}`}>
                                        {priority.label}
                                      </span>
                                    )}
                                    {due && (
                                      <span className="text-[10px] text-gray-500">{due}</span>
                                    )}
                                    {task.comments?.length > 0 && (
                                      <span className="flex items-center gap-0.5 text-[10px] text-gray-600">
                                        <MessageSquare className="w-3 h-3" />{task.comments.length}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                {hasDetails && <ChevronRight className="w-4 h-4 text-gray-600 flex-shrink-0" />}
                              </div>

                              {/* Desktop row (hidden on mobile) */}
                              <div className={`hidden sm:grid grid-cols-12 gap-2 px-4 py-3 text-sm items-center transition-colors ${
                                hasDetails ? 'hover:bg-white/[0.03]' : 'hover:bg-white/[0.02]'
                              }`}>
                                <div className="col-span-5 flex items-center gap-2 min-w-0">
                                  {hasDetails ? (
                                    expanded
                                      ? <ChevronDown  className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
                                      : <ChevronRight className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
                                  ) : (
                                    done
                                      ? <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                                      : <Circle       className="w-4 h-4 text-gray-600 flex-shrink-0" />
                                  )}
                                  <span className={`truncate ${done ? 'line-through text-gray-500' : 'text-white'}`}>
                                    {task.title}
                                  </span>
                                  {task.comments?.length > 0 && (
                                    <span className="flex items-center gap-0.5 text-xs text-gray-600 flex-shrink-0">
                                      <MessageSquare className="w-3 h-3" />{task.comments.length}
                                    </span>
                                  )}
                                </div>
                                <div className="col-span-3">
                                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${status.color}`}>
                                    {status.label}
                                  </span>
                                </div>
                                <div className="col-span-2">
                                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${priority.color}`}>
                                    {priority.label}
                                  </span>
                                </div>
                                <div className="col-span-2 text-gray-400 text-xs">
                                  {due
                                    ? <>{due}{task.due_time && <span className="text-gray-600"> {task.due_time}</span>}</>
                                    : <span className="text-gray-700">-</span>
                                  }
                                </div>
                              </div>

                              {/* Expanded detail */}
                              {expanded && hasDetails && (
                                <div className="px-6 pb-4 pt-2 bg-white/[0.015] space-y-3">
                                  {descText && (
                                    <div>
                                      <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-2">Description</p>
                                      <RichText content={task.description} />
                                    </div>
                                  )}
                                  {notesText && (
                                    <div>
                                      <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-2">Notes</p>
                                      <RichText content={task.notes} />
                                    </div>
                                  )}
                                  {task.due_time && !descText && !notesText && (
                                    <div>
                                      <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-1">Time</p>
                                      <p className="text-sm text-gray-300">{task.due_time}</p>
                                    </div>
                                  )}
                                  {task.comments?.length > 0 && (
                                    <div>
                                      <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-2">
                                        Comments ({task.comments.length})
                                      </p>
                                      <div className="space-y-3">
                                        {task.comments.map(comment => (
                                          <div key={comment.id} className="flex gap-3">
                                            <div className="w-6 h-6 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0 text-xs text-amber-400 font-medium">
                                              {(comment.user_name || '?')[0].toUpperCase()}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                              <div className="flex items-baseline gap-2">
                                                <span className="text-xs font-medium text-gray-300">
                                                  {comment.user_name || 'Unknown'}
                                                </span>
                                                <span className="text-xs text-gray-600">{formatDateTime(comment.created_at)}</span>
                                              </div>
                                              <p className="text-sm text-gray-400 mt-0.5 whitespace-pre-wrap break-words">
                                                {extractMultilineText(comment.content)}
                                              </p>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        )}
      </main>

      <footer className="border-t border-gray-800/50 mt-12 px-4 py-5 text-center">
        <p className="text-xs text-gray-600">
          Powered by{' '}
          <Link to="/" className="text-amber-500/70 hover:text-amber-400 transition-colors">BusyBee.Day</Link>
          {' '}·{' '}
          <Link to="/register" className="text-gray-500 hover:text-gray-400 transition-colors">Create your free account</Link>
        </p>
      </footer>
    </div>
  );
};

export default SharedProjectPage;
